import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { formatDate } from '@/utils/format';
import type { FlatRateAgreement, Applicant, Consumer, TerminateRequest, TerminateRequestParams } from "../types";
import { DateFieldBox } from "@/components/DateField";
import { TextAreaBox } from "@/components/TextArea";
import InfoDisplay from "@/components/InfoDisplay";
import SidebarNavigation from "@/components/SidebarNavigation";
import Layout from "../../shared/components/Layout";
import AlertMessages from "@/components/AlertMessages";
import getTerminate from "../api/getTerminate";
import type { Alert } from "@/types";
import clearLocalStorage from "@/utils/clearLocalStorage";
import NotFound from "@/template/NotFound";
import confirmTerminate from "../api/confirmTerminate";
import { getFormattedToday } from "@/utils/format";

const Terminate: React.FC = () => {
  const { custCode } = useParams();
  const [isLoading, setIsLoading] = useState(true);
  const [isNotFound, setIsNotFound] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  const [alert, setAlert] = useState<Alert>({
    open: false,
    messages: [],
  })

  const [flatRateAgreement, setFlatRateAgreement] = useState<FlatRateAgreement>();
  const [applicant, setApplicant] = useState<Applicant>();
  const [consumer, setConsumer] = useState<Consumer>();
  const[terminateRequest, setTerminateRequest] = useState<TerminateRequest>({
    lensCollectionExpectedDate: getFormattedToday(true),
    endMonth: "",
    reasons: ""
  })

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        const response = await getTerminate(custCode);
        setFlatRateAgreement(response.data.flatRateAgreement);
        setApplicant(response.data.applicant);
        setConsumer(response.data.consumer);

        setIsLoading(false);
      } catch (error: any) {
        setIsLoading(false);
        if (error.response && error.response.status === 404) {
          setIsNotFound(true);
        }
        console.error("Error fetching flat rate lens data:", error);
      }
    };

    fetchData();

    const fromConfirmPage = location.state?.fromConfirmPage;
    if(fromConfirmPage) {
      const storedApplicant = localStorage.getItem("applicant");
      if(storedApplicant) {
        setApplicant(JSON.parse(storedApplicant));
      }

      const storedConsumer = localStorage.getItem("consumer");
      if(storedConsumer) {
        setConsumer(JSON.parse(storedConsumer));
      }

      const storedTerminateRequest = localStorage.getItem("terminateRequest");
      if(storedTerminateRequest) {
        setTerminateRequest(JSON.parse(storedTerminateRequest));
      }

      const storedFlatRateAgreement = localStorage.getItem("flatRateAgreement");
      if(storedFlatRateAgreement) {
        setFlatRateAgreement(JSON.parse(storedFlatRateAgreement));
      }
    } else {
        clearLocalStorage();
      }
  }, [custCode, location.state?.fromConfirmPage]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const params: TerminateRequestParams = {
      terminateRequest: terminateRequest
    }

    try {
      await confirmTerminate(params, custCode);
      setAlert({open: false, messages: []});
      localStorage.setItem("flatRateAgreement", JSON.stringify(flatRateAgreement));
      localStorage.setItem("applicant", JSON.stringify(applicant));
      localStorage.setItem("consumer", JSON.stringify(consumer));
      localStorage.setItem("terminateRequest", JSON.stringify(terminateRequest));

      navigate(`/facility/flat_rates/terminate_cancels/${custCode}/terminate/confirm`);
      window.scrollTo(0, 0);
    } catch (err: any) {
      if (err.response && err.response.data && err.response.data.errors) {
        setAlert({open: true, messages: err.response.data.errors.fullMessages});
        window.scrollTo(0, 0);
      } else {
        setAlert({open: false, messages: []});
        console.log('An unexpected error occurred:', err);
      }
    }
  }

  const displayPlan = (plan: string, price: string): string => {
    if(plan === 'both') return `両眼プラン(${price}円/月)`
    if(plan === 'right') return `片眼プラン(右眼)(${price}円/月)`
    if(plan === 'left') return `片眼プラン(左眼)(${price}円/月)`
    return '';
  }

  if (isLoading) {
    return <></>;
  }
  if (isNotFound) {
    return <NotFound />;
  }


  return (
    <Layout>
      <main className="wrapper" id="page-facility_login">
        <section className="sec-contact">
          <div className="inner">
            <h1 className="c-ttl__01">解約申込入力</h1>
            <AlertMessages open={alert.open} messages={alert.messages} />
            <div className="contact-box">
              <div className="contact-box__left">
                <div className="mbpc-98 mbsp-32">
                  <form onSubmit={handleSubmit}>
                    <div className="contact-box__form js-item" id="id-01">
                      <h2 className="c-ttl__02">お客様情報</h2>
                      <InfoDisplay label="契約種別" value={"定額制契約"} />
                      <InfoDisplay label="お客様ID" value={custCode} />
                      <InfoDisplay label="申込者名(カナ)" value={applicant?.nameKana} />
                      <InfoDisplay label="レンズ使用者名(カナ)" value={consumer?.nameKana} />
                    </div>
                    <div className="contact-box__form mbsp-29 js-item h-adr" id="id-02">
                      <h2 className="c-ttl__02">契約詳細</h2>
                      <InfoDisplay label="ご契約プラン" value={displayPlan(flatRateAgreement?.plan ?? '', flatRateAgreement?.price?.toString() ?? '')} />
                      <InfoDisplay label="サービス利用開始日" value={formatDate(flatRateAgreement?.startDate ?? '')} />
                      <DateFieldBox
                        label="レンズ回収予定日"
                        pattern={"d{4}-d{2}-d{2}"}
                        value={terminateRequest.lensCollectionExpectedDate}
                        onChange={event => setTerminateRequest({...terminateRequest, lensCollectionExpectedDate: event.target.value})}
                        required={true}
                      />
                      <DateFieldBox
                        type="month"
                        label={"サービス利用終了月"}
                        value={terminateRequest.endMonth}
                        required={true}
                        onChange={event => {setTerminateRequest({...terminateRequest, endMonth: event.target.value})}}
                      />
                      <TextAreaBox
                        label={"解約理由"}
                        placeholder={"解約理由をご記入ください。"}
                        value={terminateRequest.reasons}
                        onChange={event => {setTerminateRequest({...terminateRequest, reasons: event.target.value})}}
                        note={"※サービスのご利用開始月より12ヵ月以内の解約の場合、こちらに理由をご記入ください"}
                      />
                    </div>
                    <button type="submit" className="c-btn__01">入力内容の確認</button>
                  </form>
                </div>
              </div>
              <SidebarNavigation items={[
                { id: "id-01", label: "お客様情報" },
                { id: "id-02", label: "契約詳細" },
                ]} />
            </div>
          </div>
        </section>
      </main>
    </Layout>
  )
}

export default Terminate;